/**
 * Chart Utilities
 * Helper functions for data transformation and formatting
 */

import { chartTheme } from './chart-theme'

/**
 * Format currency value in Brazilian Real
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value)
}

/**
 * Format chart axis labels
 */
export const formatChartAxis = (value: number, type: 'currency' | 'percentage' | 'number'): string => {
  switch (type) {
    case 'currency':
      return formatCurrency(value)
    case 'percentage':
      return `${value.toFixed(1)}%`
    case 'number':
      return value.toLocaleString('pt-BR')
    default:
      return String(value)
  }
}

/**
 * Calculate percentage change between two values
 */
export const calculatePercentChange = (current: number, previous: number): number => {
  if (previous === 0) return 0
  return ((current - previous) / previous) * 100
}

/**
 * Get color based on variance percentage
 * Used for gauges and indicators
 */
export const getVarianceColor = (percentage: number): string => {
  if (percentage < 70) return '#EF4444'      // Red
  if (percentage < 90) return '#F59E0B'      // Yellow
  if (percentage < 110) return '#10B981'     // Green
  return '#059669'                            // Bright Green
}

/**
 * Get heatmap color based on variance percentage
 * More granular scale for heatmap cells
 */
export const getHeatmapColor = (variance: number): string => {
  const scale = chartTheme.colors.varianceScale

  if (variance <= -50) return scale[0]       // Deep red
  if (variance <= -25) return scale[1]       // Red
  if (variance <= -10) return scale[2]       // Light red
  if (variance <= 10) return scale[3]        // Yellow
  if (variance <= 25) return scale[4]        // Light green
  if (variance <= 50) return scale[5]        // Green
  return scale[6]                            // Deep green
}

/**
 * Transform API data for chart consumption
 * Generic transformation function
 */
export const formatChartData = <T extends Record<string, any>>(
  data: T[],
  xKey: keyof T,
  yKeys: (keyof T)[]
): any[] => {
  return data.map(item => {
    const chartItem: Record<string, any> = {
      [xKey]: item[xKey]
    }

    yKeys.forEach(key => {
      chartItem[String(key)] = item[key]
    })

    return chartItem
  })
}

/**
 * Calculate attainment percentage (actual / target * 100)
 */
export const calculateAttainment = (actual: number, target: number): number => {
  if (target === 0) return 0
  return (actual / target) * 100
}

/**
 * Truncate long text for chart labels
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength - 3)}...`
}
